import { useCallback, useState } from 'react'

export interface LocationsSelection {
  selected: ReadonlySet<number>
  toggle: (index: number) => void
  setMany: (indices: number[], isSelected: boolean) => void
  clear: () => void
}

const EMPTY_SELECTION: ReadonlySet<number> = new Set()

/**
 * Selection of rows by their index in the Formik array.
 * Indices move when a location is added or removed, so the selection is dropped whenever the list length changes.
 */
export function useLocationsSelection(count: number): LocationsSelection {
  const [state, setState] = useState<{ count: number; selected: ReadonlySet<number> }>({
    count,
    selected: EMPTY_SELECTION,
  })
  const selected = state.count === count ? state.selected : EMPTY_SELECTION

  const update = useCallback(
    (change: (current: Set<number>) => void) =>
      setState((previous) => {
        const next = new Set(previous.count === count ? previous.selected : EMPTY_SELECTION)
        change(next)
        return { count, selected: next }
      }),
    [count]
  )

  const toggle = useCallback(
    (index: number) =>
      update((next) => {
        if (next.has(index)) next.delete(index)
        else next.add(index)
      }),
    [update]
  )

  const setMany = useCallback(
    (indices: number[], isSelected: boolean) =>
      update((next) => indices.forEach((index) => (isSelected ? next.add(index) : next.delete(index)))),
    [update]
  )

  const clear = useCallback(() => setState({ count, selected: EMPTY_SELECTION }), [count])

  return { selected, toggle, setMany, clear }
}
